import React, { useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import './App.css';
import Chatbot from './Chatbot';
import Sidebar from './components/Sidebar';
import ResumeScorerResults from './components/ResumeScorerResults';
import AvatarModal from './components/AvatarModal';

const SUGGESTED_TOPICS = [
  "Prompt Engineering",
  "Generative AI & RAG",
  "Quantum Computing",
  "Zero-Knowledge Proofs",
  "Space Exploration",
  "CRISPR Gene Editing",
  "Brain-Computer Interfaces"
];

function App() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  const [formData, setFormData] = useState({
    name: '',
    resume: null,
    topic: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [mode, setMode] = useState('interviewer');
  const [showAvatar, setShowAvatar] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [beyAgentId, setBeyAgentId] = useState('');
  const [beyEmbedUrl, setBeyEmbedUrl] = useState('');
  const [resolvedEmbedUrl, setResolvedEmbedUrl] = useState('');
  const [agentStatus, setAgentStatus] = useState('unchecked');
  const [scorerResult, setScorerResult] = useState(null);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  React.useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config`);
      if (!response.ok) throw new Error('Unable to fetch config');
      const data = await response.json();
      setBeyAgentId((data?.beyAgentId || '').trim());
      setBeyEmbedUrl((data?.beyEmbedUrl || '').trim());
      setResolvedEmbedUrl((data?.resolvedEmbedUrl || '').trim());
      setAgentStatus((data?.agentStatus || 'unchecked').trim());
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode !== 'tutor' && !formData.resume) {
        alert('Please upload a resume (PDF).');
        return;
    }
    if (mode === 'tutor' && !formData.topic && !formData.resume) {
        alert('Please enter a topic or upload a PDF for the AI Tutor.');
        return;
    }
    setSubmitted(true);
    setResolvedEmbedUrl('');
    try {
      const data = new FormData();
      data.append('name', formData.name);
      
      if (mode === 'tutor') {
        data.append('topic', formData.topic);
        if (formData.resume) {
          data.append('file', formData.resume);
        }
      } else {
        data.append('resume', formData.resume);
      }

      let endpoint = '';
      if (mode === 'interviewer') {
        endpoint = `${API_BASE_URL}/api/interview/start`;
      } else if (mode === 'counsellor') {
        endpoint = `${API_BASE_URL}/api/counsellor/start`;
      } else if (mode === 'tutor') {
        endpoint = `${API_BASE_URL}/api/tutor/start`;
      } else {
        endpoint = `${API_BASE_URL}/api/resume/score`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        body: data,
      });

      if (!response.ok) throw new Error(`Failed to start ${mode}`);

      const result = await response.json();
      
      if (mode === 'scorer') {
        setScorerResult(result);
        setSubmitted(false);
        return;
      }
      
      setCurrentSessionId(result.id);
      if (result.resolvedEmbedUrl) {
        setResolvedEmbedUrl(result.resolvedEmbedUrl);
      }
      setShowAvatar(true);

    } catch (error) {
      console.error('Error:', error);
      alert('There was an error connecting to the server. Please check if the backend is running.');
      setSubmitted(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume') {
        setFormData({ ...formData, resume: files[0] });
    } else {
        setFormData({ ...formData, [name]: value });
    }
  };

  const withMetadata = (baseUrl) => {
    const separator = baseUrl.includes('?') ? '&' : '?';
    let titleStr = '';
    if (mode === 'tutor') titleStr = '&title=AI+Tutor&name=AI+Tutor';
    else if (mode === 'counsellor') titleStr = '&title=AI+Counsellor&name=AI+Counsellor';
    else titleStr = '&title=AI+Interviewer&name=AI+Interviewer';
    
    return `${baseUrl}${separator}metadata=${encodeURIComponent(
      JSON.stringify({ externalId: currentSessionId })
    )}${titleStr}`;
  };

  const avatarUrl = resolvedEmbedUrl
    ? withMetadata(resolvedEmbedUrl)
    : beyEmbedUrl
      ? withMetadata(beyEmbedUrl)
    : beyAgentId
      ? withMetadata(`https://bey.chat/${beyAgentId}`)
    : '';

  return (
    <div className="dashboard-container">
      <button className="theme-toggle-btn top-right" onClick={toggleTheme}>
        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
      </button>
      
      <Sidebar 
        mode={mode} 
        setMode={setMode} 
        setScorerResult={setScorerResult} 
        theme={theme}
      />

      <main className="main-content">
        <header className="welcome-section">
          <div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Welcome back,</p>
            <h1>{mode === 'interviewer' ? 'AI Interview' : mode === 'counsellor' ? 'AI Counselling Session' : mode === 'tutor' ? 'AI Tutor' : 'AI Resume Scorer'}</h1>
          </div>
        </header>

        <section style={{ display: 'grid', gridTemplateColumns: mode === 'scorer' && scorerResult ? '1fr' : '1fr 400px', gap: '2rem' }}>
          {mode === 'scorer' && scorerResult ? (
            <ResumeScorerResults scorerResult={scorerResult} setScorerResult={setScorerResult} />
          ) : (
            <div className="card">
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>
                  {mode === 'interviewer' ? 'AI Interview' : mode === 'counsellor' ? 'AI Counsellor' : mode === 'tutor' ? 'AI Tutor' : 'AI Resume Scorer'}
                </h3>
                <p style={{ color: 'var(--text-secondary)' }}>{mode === 'tutor' ? 'Enter your details and the topic you want to learn.' : 'Enter your details and upload your resume.'}</p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="e.g. Alex Rivera"
                    className="input-field"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>


                {mode === 'tutor' ? (
                  <div className="form-row">
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <label>Topic to Learn</label>
                      <input
                        type="text"
                        name="topic"
                        list="topic-suggestions"
                        placeholder="e.g. Quantum Physics, Machine Learning"
                        className="input-field"
                        value={formData.topic}
                        onChange={handleChange}
                        style={{ flex: 1 }}
                      />
                      <datalist id="topic-suggestions">
                        {SUGGESTED_TOPICS.map((t) => (
                          <option key={t} value={t} />
                        ))}
                      </datalist>
                    </div>
                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <label>Or Upload Notes (PDF)</label>
                      {!formData.resume ? (
                        <div className="custom-upload-container" style={{ flex: 1, display: 'flex' }}>
                          <input
                            type="file"
                            id="notes-upload"
                            name="resume"
                            accept="application/pdf"
                            className="hidden-file-input"
                            onChange={handleChange}
                          />
                          <label htmlFor="notes-upload" className="upload-placeholder" style={{ flex: 1, margin: 0 }}>
                            <span>UPLOAD NOTES (PDF)</span>
                          </label>
                        </div>
                      ) : (
                        <div className="file-status" style={{ flex: 1, margin: 0 }}>
                          <span className="file-name">📄 {formData.resume.name}</span>
                          <button 
                            type="button" 
                            className="change-file-btn"
                            onClick={() => setFormData({ ...formData, resume: null })}
                          >
                            Change
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Resume (PDF)</label>
                    {!formData.resume ? (
                      <div className="custom-upload-container">
                        <input
                          type="file"
                          id="resume-upload"
                          name="resume"
                          accept="application/pdf"
                          className="hidden-file-input"
                          onChange={handleChange}
                          required
                        />
                        <label htmlFor="resume-upload" className="upload-placeholder">
                          <span>Click to upload your resume</span>
                        </label>
                      </div>
                    ) : (
                      <div className="file-status">
                        <span className="file-name">📄 {formData.resume.name}</span>
                        <button 
                          type="button" 
                          className="change-file-btn"
                          onClick={() => setFormData({ ...formData, resume: null })}
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <button type="submit" className="submit-btn" disabled={submitted}>
                  {submitted ? '✅ Processing...' : 
                   mode === 'scorer' ? 'Analyze Resume' : 
                   `Start ${mode === 'interviewer' ? 'Interview' : mode === 'tutor' ? 'Tutor' : 'Session'}`}
                </button>
              </form>
            </div>
          )}
        </section>
      </main>

      <AvatarModal 
        showAvatar={showAvatar} 
        setShowAvatar={setShowAvatar} 
        mode={mode} 
        avatarUrl={avatarUrl} 
        setFormData={setFormData} 
        setSubmitted={setSubmitted} 
      />

      <Chatbot />
    </div>
  );
}

export default App;
